import React, { useCallback, useEffect } from 'react';
import _, { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  Fab,
  FormHelperText,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import usePrevious from '../../../hooks/usePrevious';

import * as lookupSlice from '../../../data/lookup.slice';
import { StoreStatus } from '../../../data/types';

import { validateBC } from '../../../services/validationUtils';

import PageHeader from '../../controls/PageHeader';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Lookups';

const classes = {
  container: `${PREFIX}-container`,
  dropdown: `${PREFIX}-dropdown`,
  content: `${PREFIX}-content`,
  grid: `${PREFIX}-grid`,
  footer: `${PREFIX}-footer`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  [`& .${classes.dropdown}`]: {
    width: 400,
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.background.default,
    },
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
  },
  [`& .${classes.grid}`]: {
    width: '100%',
  },
  [`& .${classes.footer}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
}));

interface LookupConfig {
  tableName: string;
  // values: any[];
  hasDescription: boolean;
  hasDisplayOrder: boolean;
  hasExtension?: boolean;
  hasTripMode?: boolean;
  hasTimeZone?: boolean;
  hasPriorityOrder?: boolean;
  hideBC?: boolean;
  hideName?: boolean;
  disableEdit?: boolean;
  disableAddNew?: boolean;
}

interface LookupRow {
  lookupID?: string;
  lookupName: string;
  lookupBC: string;
  cronJobSchedule?: string;
  description?: string;
  displayOrder?: number;
  priorityOrder?: number;
  ianaStandard?: string;
  isActive: boolean;
  isEffectiveDatesRequired?: boolean;
  isRequired?: boolean;
  lookupExtension?: string;
  reservationRetryLimit?: number;
  stateCode?: string;
  stateName?: string;
  timeZoneDayLight?: string;
  timeZoneStandard?: string;
  tripMode?: string;
}

export default function Lookups() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();
  const isSuperReaderUser = useIsSuperReaderUser();

  const [currentConfig, setCurrentConfig] = React.useState<LookupConfig>();
  const [rows, setRows] = React.useState<LookupRow[]>();
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingNewItem, setIsEditingNewItem] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const storeStatus = useAppSelector(lookupSlice.selectLookupStatus);
  const previousStoreStatus = usePrevious(storeStatus);

  useEffect(() => {
    if (
      previousStoreStatus === StoreStatus.Creating &&
      storeStatus === StoreStatus.Error
    ) {
      setRows((oldRows) => oldRows?.slice(0, oldRows.length - 1));
      dispatch(lookupSlice.setStatus(StoreStatus.Idle));
      setIsEditingNewItem(false);
    }
  }, [storeStatus, previousStoreStatus]);

  useEffect(() => {
    if ([StoreStatus.Idle, StoreStatus.Success].includes(storeStatus)) {
      setIsEditingNewItem(false);
    }
  }, [storeStatus]);

  // ********************************************************************
  // Add an entry here for each lookup that corresponds to the tableName
  // in the lookupConfigs below.
  const lookupValues: any = {
    alertEvent: useAppSelector(lookupSlice.selectAlertEventsUnfiltered),
    alertType: useAppSelector(lookupSlice.selectAlertTypesUnfiltered),
    alternateContactType: useAppSelector(
      lookupSlice.selectAlternateContactTypesUnfiltered,
    ),
    applicationSetting: useAppSelector(
      lookupSlice.selectApplicationSettingsUnfiltered,
    ),
    benefitPurpose: useAppSelector(lookupSlice.selectBenefitPurposesUnfiltered),
    callOutcome: useAppSelector(lookupSlice.selectCallOutcomesUnfiltered),
    chargeType: useAppSelector(lookupSlice.selectChargeTypesUnfiltered),
    clientAddressUse: useAppSelector(
      lookupSlice.selectClientAddressUsesUnfiltered,
    ),
    clientContactDepartment: useAppSelector(
      lookupSlice.selectClientContactDepartmentsUnfiltered,
    ),
    clientContactRole: useAppSelector(
      lookupSlice.selectClientContactRolesUnfiltered,
    ),
    clientVertical: useAppSelector(lookupSlice.selectClientVerticalsUnfiltered),
    communicationType: useAppSelector(
      lookupSlice.selectCommunicationTypesUnfiltered,
    ),
    correspondenceReason: useAppSelector(
      lookupSlice.selectCorrespondenceReasonsUnfiltered,
    ),
    discussionAcceptReason: useAppSelector(
      lookupSlice.selectDiscussionAcceptReasonsUnfiltered,
    ),
    discussionDecisionOutcome: useAppSelector(
      lookupSlice.selectDiscussionDecisionOutcomesUnfiltered,
    ),
    discussionRejectReason: useAppSelector(
      lookupSlice.selectDiscussionRejectReasonsUnfiltered,
    ),
    driverDocumentType: useAppSelector(
      lookupSlice.selectDriverDocumentTypesUnfiltered,
    ),
    eventTypeClassification: useAppSelector(
      lookupSlice.selectEventTypeClassificaitonsUnfiltered,
    ),
    fileIngestionFrequency: useAppSelector(
      lookupSlice.selectFileIngestionFrequenciesUnfiltered,
    ),
    fileType: useAppSelector(lookupSlice.selectFileTypesUnfiltered),
    frequencyDayOfWeek: useAppSelector(
      lookupSlice.selectFrequencyDayOfWeeksUnfiltered,
    ),
    frequencyPeriod: useAppSelector(
      lookupSlice.selectFrequencyPeriodsUnfiltered,
    ),
    ingestionCronJob: useAppSelector(
      lookupSlice.selectIngestionCronJobsUnfiltered,
    ),
    insuranceLimitType: useAppSelector(
      lookupSlice.selectInsuranceLimitTypesUnfiltered,
    ),
    insuranceStrength: useAppSelector(
      lookupSlice.selectInsuranceStrengthsUnfiltered,
    ),
    insuranceType: useAppSelector(lookupSlice.selectInsuranceTypesUnfiltered),
    legalEntityStatus: useAppSelector(
      lookupSlice.selectLegalEntityStatusUnfiltered,
    ),
    legalEntityType: useAppSelector(
      lookupSlice.selectLegalEntityTypeUnfiltered,
    ),
    lineOfBusiness: useAppSelector(lookupSlice.selectLinesOfBusinessUnfiltered),
    messagingMethod: useAppSelector(
      lookupSlice.selectMessagingMethodsUnfiltered,
    ),
    passengerAuthorizationReason: useAppSelector(
      lookupSlice.selectPassengerAuthorizationReasonsUnfiltered,
    ),
    passengerPhoneUse: useAppSelector(
      lookupSlice.selectPassengerPhoneUsesUnfiltered,
    ),
    passengerAddressUse: useAppSelector(
      lookupSlice.selectPassengerAddressUsesUnfiltered,
    ),
    paymentType: useAppSelector(lookupSlice.selectPaymentTypesUnfiltered),
    permissionAction: useAppSelector(
      lookupSlice.selectPermissionActionsUnfiltered,
    ),
    permissionResource: useAppSelector(
      lookupSlice.selectPermissionResourcesUnfiltered,
    ),
    permissionScope: useAppSelector(
      lookupSlice.selectPermissionScopesUnfiltered,
    ),
    phoneNumberType: useAppSelector(
      lookupSlice.selectPhoneNumberTypesUnfiltered,
    ),
    pricingCalculationGroup: useAppSelector(
      lookupSlice.selectPricingCalculationGroupsUnfiltered,
    ),
    purposeCodeCategory: useAppSelector(
      lookupSlice.selectPurposeCodeCategoriesUnfiltered,
    ),
    purposeCodeType: useAppSelector(
      lookupSlice.selectPurposeCodeTypesUnfiltered,
    ),
    smsStatus: useAppSelector(lookupSlice.selectSmsStatusesUnfiltered),
    stateCodeList: useAppSelector(lookupSlice.selectStateCodesUnfiltered),
    transportationProviderAddressUse: useAppSelector(
      lookupSlice.selectTransportationProviderAddressUsesUnfiltered,
    ),
    transportationProviderDocumentType: useAppSelector(
      lookupSlice.selectTransportationProviderDocumentTypesUnfiltered,
    ),
    transportationProviderEmailUse: useAppSelector(
      lookupSlice.selectTransportationProviderEmailUsesUnfiltered,
    ),
    transportationProviderPhoneUse: useAppSelector(
      lookupSlice.selectTransportationProviderPhoneUsesUnfiltered,
    ),
    transportationProviderTier: useAppSelector(
      lookupSlice.selectTransportationProviderTiersUnfiltered,
    ),
    transportationType: useAppSelector(
      lookupSlice.selectTransportationTypesUnfiltered,
    ),
    taskEscalateReason: useAppSelector(
      lookupSlice.selectTaskEscalateReasonsUnfiltered,
    ),
    taskResolveReason: useAppSelector(
      lookupSlice.selectTaskResolveReasonsUnfiltered,
    ),
    timeZone: useAppSelector(lookupSlice.selectTimeZonesUnfiltered),
    transportationProviderPayStatus: useAppSelector(
      lookupSlice.selectTransportationProviderPayStatusesUnfiltered,
    ),
    tripDestinationCategory: useAppSelector(
      lookupSlice.selectTripDestinationCategoriesUnfiltered,
    ),
    tripLegAdjustmentType: useAppSelector(
      lookupSlice.selectTripLegAdjustmenTypes,
    ),
    tripLegDriverNoteType: useAppSelector(
      lookupSlice.selectDriverNoteTypesUnfiltered,
    ),
    tripLegException: useAppSelector(
      lookupSlice.selectTripLegExceptionsUnfiltered,
    ),
    tripLegOverrideReason: useAppSelector(
      lookupSlice.selectOverrideAuthorizationReasonsUnfiltered,
    ),
    tripLegPackageRejectReason: useAppSelector(
      lookupSlice.selectTripLegPackageRejectReasonsUnfiltered,
    ),
    tripLegPackageStatus: useAppSelector(
      lookupSlice.selectTripLegPackageStatusesUnfiltered,
    ),
    tripLegPassengerNoteType: useAppSelector(
      lookupSlice.selectPassengerNoteTypesUnfiltered,
    ),
    tripLegStatus: useAppSelector(lookupSlice.selectTripLegStatusesUnfiltered),
    tripLegStatusReason: useAppSelector(
      lookupSlice.selectTripLegStatusReasonsUnfiltered,
    ),
    tripMode: useAppSelector(lookupSlice.selectTripModesUnfiltered),
    vehicleAttributeType: useAppSelector(
      lookupSlice.selectVehicleAttributeTypesUnfiltered,
    ),
    vehicleColor: useAppSelector(lookupSlice.selectVehicleColorsUnfiltered),
    vehicleDocumentType: useAppSelector(
      lookupSlice.selectVehicleDocumentTypesUnfiltered,
    ),
    vehicleType: useAppSelector(lookupSlice.selectVehicleTypesUnfiltered),
  };
  // ********************************************************************

  // ********************************************************************
  // Update this configuration array to add new lookups to edit.
  const lookupConfigs: LookupConfig[] = [
    {
      tableName: 'alertEvent',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'alertType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'alternateContactType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'applicationSetting',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'benefitPurpose',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'callOutcome',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'chargeType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'clientAddressUse',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'clientContactDepartment',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'clientContactRole',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'clientVertical',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'communicationType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'correspondenceReason',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'discussionAcceptReason',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'discussionDecisionOutcome',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'discussionRejectReason',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'driverDocumentType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'eventTypeClassification',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'fileIngestionFrequency',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'fileType',
      hasDescription: false,
      hasDisplayOrder: false,
      hasExtension: true,
    },
    {
      tableName: 'frequencyDayOfWeek',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'frequencyPeriod',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'ingestionCronJob',
      hasDescription: true,
      hasDisplayOrder: false,
      hideName: true,
    },
    {
      tableName: 'insuranceLimitType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'insuranceStrength',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'insuranceType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'legalEntityStatus',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'legalEntityType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'lineOfBusiness',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'messagingMethod',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'passengerAuthorizationReason',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'passengerPhoneUse',
      hasDescription: true,
      hasDisplayOrder: true,
    },
    {
      tableName: 'passengerAddressUse',
      hasDescription: true,
      hasDisplayOrder: true,
    },
    {
      tableName: 'paymentType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'permissionAction',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'permissionResource',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'permissionScope',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'phoneNumberType',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'pricingCalculationGroup',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'purposeCodeCategory',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'purposeCodeType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'smsStatus',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'stateCodeList',
      hasDescription: false,
      hasDisplayOrder: false,
      hideBC: true,
      hideName: true,
      disableEdit: true,
      disableAddNew: true,
    },
    {
      tableName: 'taskEscalateReason',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'taskResolveReason',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'timeZone',
      hasDescription: false,
      hasDisplayOrder: false,
      hasTimeZone: true,
    },
    {
      tableName: 'transportationProviderAddressUse',
      hasDescription: true,
      hasDisplayOrder: true,
    },
    {
      tableName: 'transportationProviderDocumentType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'transportationProviderEmailUse',
      hasDescription: true,
      hasDisplayOrder: true,
    },
    {
      tableName: 'transportationProviderPayStatus',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'transportationProviderPhoneUse',
      hasDescription: true,
      hasDisplayOrder: true,
    },
    {
      tableName: 'transportationProviderTier',
      hasDescription: true,
      hasDisplayOrder: true,
      hasPriorityOrder: true,
    },
    {
      tableName: 'transportationType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripDestinationCategory',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripMode',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegAdjustmentType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegDriverNoteType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegException',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegOverrideReason',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'tripLegPackageRejectReason',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegPackageStatus',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegPassengerNoteType',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegStatus',
      hasDescription: true,
      hasDisplayOrder: false,
    },
    {
      tableName: 'tripLegStatusReason',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'vehicleAttributeType',
      hasDescription: false,
      hasDisplayOrder: true,
    },
    {
      tableName: 'vehicleColor',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'vehicleDocumentType',
      hasDescription: false,
      hasDisplayOrder: false,
    },
    {
      tableName: 'vehicleType',
      hasDescription: true,
      hasDisplayOrder: false,
      hasTripMode: true,
    },
  ];
  // ********************************************************************

  React.useEffect(() => {
    lookupConfigs.forEach((config) => {
      dispatch(lookupSlice.fetchLookupItemsAsync(config.tableName));
    });
  }, []);

  React.useEffect(() => {
    if (rows && currentConfig) {
      if (lookupValues[currentConfig.tableName].length > rows.length) {
        updateRows(currentConfig);
      }
    }
  }, [lookupValues]);

  React.useEffect(() => {
    apiRef.current.setColumnVisibility(
      'description',
      !!currentConfig?.hasDescription,
    );
    apiRef.current.setColumnVisibility(
      'displayOrder',
      !!currentConfig?.hasDisplayOrder,
    );
    apiRef.current.setColumnVisibility(
      'priorityOrder',
      !!currentConfig?.hasPriorityOrder,
    );
    apiRef.current.setColumnVisibility(
      'lookupExtension',
      !!currentConfig?.hasExtension,
    );
    apiRef.current.setColumnVisibility(
      'tripMode',
      !!currentConfig?.hasTripMode,
    );
    apiRef.current.setColumnVisibility(
      'ianaStandard',
      !!currentConfig?.hasTimeZone,
    );
    apiRef.current.setColumnVisibility(
      'timeZoneDayLight',
      !!currentConfig?.hasTimeZone,
    );
    apiRef.current.setColumnVisibility(
      'timeZoneStandard',
      !!currentConfig?.hasTimeZone,
    );
    apiRef.current.setColumnVisibility('lookupBC', !currentConfig?.hideBC);
    apiRef.current.setColumnVisibility('lookupName', !currentConfig?.hideName);
    apiRef.current.setColumnVisibility(
      'cronJobSchedule',
      currentConfig?.tableName === 'ingestionCronJob',
    );
    apiRef.current.setColumnVisibility(
      'stateCode',
      currentConfig?.tableName === 'stateCodeList',
    );
    apiRef.current.setColumnVisibility(
      'stateName',
      currentConfig?.tableName === 'stateCodeList',
    );
    apiRef.current.setColumnVisibility(
      'reservationRetryLimit',
      currentConfig?.tableName === 'frequencyPeriod',
    );
    apiRef.current.setColumnVisibility(
      'isDeleted',
      currentConfig?.tableName === 'smsStatus',
    );
    apiRef.current.setColumnVisibility(
      'isRequired',
      [
        'driverDocumentType',
        'transportationProviderDocumentType',
        'vehicleDocumentType',
      ].includes(currentConfig?.tableName ?? ''),
    );
    apiRef.current.setColumnVisibility(
      'isEffectiveDatesRequired',
      [
        'driverDocumentType',
        'transportationProviderDocumentType',
        'vehicleDocumentType',
      ].includes(currentConfig?.tableName ?? ''),
    );
    apiRef.current.setColumnVisibility(
      'tripLegStatusID',
      currentConfig?.tableName === 'tripLegStatusReason',
    );
    apiRef.current.setColumnVisibility(
      'timeZoneID',
      currentConfig?.tableName === 'ingestionCronJob',
    );
    apiRef.current.setColumnVisibility(
      'actions',
      currentConfig?.disableEdit === true ? false : true,
    );
  }, [currentConfig, apiRef]);

  const startColumns: GridColDef[] = [
    // {
    //   field: 'lookupID',
    //   headerName: 'ID',
    //   width: 80,
    //   sortable: false,
    //   editable: false,
    // },
    {
      field: 'lookupName',
      headerName: t('settings.lookupName'),
      flex: 1,
      sortable: false,
      editable: true,
    },
  ];

  const endColumns: GridColDef[] = [
    {
      field: 'description',
      headerName: t('settings.description'),
      flex: 1,
      sortable: false,
      editable: true,
    },
    {
      field: 'displayOrder',
      headerName: t('settings.displayOrder'),
      width: 120,
      type: 'number',
      sortable: false,
      editable: true,
    },
    {
      field: 'priorityOrder',
      headerName: t('settings.priorityOrder'),
      width: 120,
      type: 'number',
      sortable: false,
      editable: true,
    },
    {
      field: 'reservationRetryLimit',
      headerName: t('settings.lookupReservationRetryLimit'),
      width: 120,
      type: 'number',
      sortable: true,
      editable: true,
    },
    {
      field: 'lookupExtension',
      headerName: t('settings.lookupExtension'),
      width: 100,
      sortable: false,
      editable: true,
    },
    {
      field: 'cronJobSchedule',
      headerName: t('settings.cronJobSchedule'),
      width: 100,
      sortable: false,
      editable: true,
    },
    {
      field: 'ianaStandard',
      headerName: t('settings.ianaStandard'),
      width: 200,
      sortable: false,
      editable: true,
    },
    // {
    //   field: 'stateCode',
    //   headerName: t('admin.stateCode'),
    //   width: 200,
    //   editable: false,
    // },
    {
      field: 'stateName',
      headerName: t('settings.stateName'),
      width: 200,
      editable: true,
    },
    {
      field: 'timeZoneDayLight',
      headerName: t('settings.timeZoneDayLight'),
      width: 120,
      sortable: false,
      editable: true,
    },
    {
      field: 'timeZoneStandard',
      headerName: t('settings.timeZoneStandard'),
      width: 120,
      sortable: false,
      editable: true,
    },
    {
      field: 'isDeleted',
      headerName: t('settings.lookupDeleted'),
      width: 80,
      type: 'boolean',
      sortable: false,
      editable: true,
    },
    {
      field: 'isRequired',
      headerName: t('settings.required'),
      width: 100,
      type: 'boolean',
      sortable: false,
      editable: true,
    },
    {
      field: 'isEffectiveDatesRequired',
      headerName: t('settings.effectiveDatesRequired'),
      width: 180,
      type: 'boolean',
      sortable: false,
      editable: true,
    },
    {
      field: 'isActive',
      headerName: t('settings.lookupActive'),
      width: 80,
      type: 'boolean',
      sortable: false,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: GridRowId }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return !isSuperReaderUser
          ? [
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                disabled={isEditing}
                onClick={handleEditClick(id)}
                color="primary"
              />,
            ]
          : [];
      },
    },
  ];

  const editColumns: GridColDef[] = [
    ...startColumns,
    {
      field: `lookupBC`,
      headerName: t('settings.lookupBC'),
      flex: 1,
      sortable: false,
      editable: false,
    },
    {
      field: 'stateCode',
      headerName: t('settings.stateCode'),
      width: 200,
      editable: false,
    },
    {
      field: 'tripMode',
      headerName: t('settings.lookupTripMode'),
      width: 200,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.tripMode,
      getOptionValue: (value: any) => value.tripModeID,
      getOptionLabel: (value: any) => value.tripModeName,
    },
    {
      field: 'tripLegStatusID',
      headerName: t('settings.tripLegStatus'),
      width: 200,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.tripLegStatus,
      getOptionValue: (value: any) => value.tripLegStatusID,
      getOptionLabel: (value: any) => value.tripLegStatusName,
    },
    {
      field: 'timeZoneID',
      headerName: t('common.timezone'),
      width: 120,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.timeZone,
      getOptionValue: (value: any) => value.timeZoneID,
      getOptionLabel: (value: any) => value.timeZoneBC,
    },
    ...endColumns,
  ];

  const createColumns: GridColDef[] = [
    ...startColumns,
    {
      field: `lookupBC`,
      headerName: t('settings.lookupBC'),
      flex: 1,
      sortable: false,
      editable: true,
    },
    {
      field: 'stateCode',
      headerName: t('settings.stateCode'),
      width: 200,
      editable: true,
    },
    {
      field: 'tripMode',
      headerName: t('settings.lookupTripMode'),
      width: 200,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.tripMode,
      getOptionValue: (value: any) => value.tripModeID,
      getOptionLabel: (value: any) => value.tripModeName,
    },
    {
      field: 'tripLegStatusID',
      headerName: t('settings.tripLegStatus'),
      width: 200,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.tripLegStatus,
      getOptionValue: (value: any) => value.tripLegStatusID,
      getOptionLabel: (value: any) => value.tripLegStatusName,
    },
    {
      field: 'timeZoneID',
      headerName: t('common.timezone'),
      width: 200,
      type: 'singleSelect',
      sortable: false,
      editable: true,
      valueOptions: lookupValues.timeZone,
      getOptionValue: (value: any) => value.timeZoneID,
      getOptionLabel: (value: any) => value.timeZoneBC,
    },
    ...endColumns,
  ];

  const updateRows = useCallback(
    (config?: LookupConfig) => {
      if (config) {
        const newRows = lookupValues[config.tableName].map(
          (val: any, idx: number) => {
            const lookupBC = val[`${config.tableName}BC`] || val.stateCode; // Workaround for stateCodeList as BC is used for id
            return {
              // Standard Fields
              lookupBC,
              lookupID: val[`${config.tableName}ID`],
              lookupExtension: val[`${config.tableName}Extension`],
              lookupName: val[`${config.tableName}Name`] || val.colorName,
              // Custom Fields
              cronJobSchedule: val.cronJobSchedule,
              description: val.description,
              displayOrder: val.displayOrder,
              priorityOrder: val.priorityOrder,
              ianaStandard: val.ianaStandard,
              isActive: val.isActive,
              isDeleted: val.isDeleted,
              isEffectiveDatesRequired: val.isEffectiveDatesRequired,
              isRequired: val.isRequired,
              reservationRetryLimit: val.reservationRetryLimit,
              stateCode: val.stateCode,
              stateName: val.stateName,
              timeZoneID: val.timeZoneID,
              timeZoneDayLight: val.timeZoneDayLight,
              timeZoneStandard: val.timeZoneStandard,
              tripMode: val.tripModeID,
              tripLegStatusID: val.tripLegStatusID,
            };
          },
        );
        setRows(newRows);
      } else {
        setRows(undefined);
      }
    },
    [lookupValues],
  );

  const endEditing = () => {
    setIsEditing(false);
    setIsEditingNewItem(false);
    setErrorMessage('');
  };

  const onChange = (newTableName: string) => {
    const config = lookupConfigs.find((c) => c.tableName === newTableName);
    setCurrentConfig(config);
    updateRows(config);
  };

  const handleEditClick = React.useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
      setIsEditing(true);
      setIsEditingNewItem(false);
    },
    [rowModesModel],
  );

  const handleSaveClick = React.useCallback(
    (id: GridRowId) => () => {
      setIsEditing(false);
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View },
      });
    },
    [rowModesModel],
  );

  const handleCancelClick = React.useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
      if (!id && rows) {
        setRows(rows.filter((row) => row.lookupBC !== id));
      }

      endEditing();
    },
    [rowModesModel],
  );

  const packageLookup = (
    config: LookupConfig,
    idx: number,
    newRow: GridValidRowModel,
  ) => {
    const lookup: any = {
      description: newRow.description,
      displayOrder: newRow.displayOrder,
      priorityOrder: newRow.priorityOrder,
      isActive: newRow.isActive,
    };
    if (idx >= 0) {
      lookup[`${config.tableName}ID`] =
        lookupValues[config.tableName][idx][`${config.tableName}ID`];
    }
    if (!['stateCodeList'].includes(currentConfig?.tableName ?? '')) {
      lookup[`${config.tableName}Name`] = newRow.lookupName;
      lookup[`${config.tableName}BC`] = newRow.lookupBC.trim();
    }
    if (config.tableName === 'ingestionCronJob') {
      delete lookup[`${config.tableName}Name`];
      lookup.timeZoneID = newRow.timeZoneID;
    }

    lookup[`${config.tableName}Extension`] = newRow.lookupExtension?.trim();
    lookup['cronJobSchedule'] = newRow.cronJobSchedule?.trim();
    lookup['ianaStandard'] = newRow.ianaStandard;
    lookup['isEffectiveDatesRequired'] = newRow.isEffectiveDatesRequired;
    lookup['isRequired'] = newRow.isRequired;
    lookup['reservationRetryLimit'] = newRow.reservationRetryLimit;
    lookup['stateCode'] = newRow.stateCode;
    lookup['stateName'] = newRow.stateName;
    lookup['timeZoneDayLight'] = newRow.timeZoneDayLight;
    lookup['timeZoneStandard'] = newRow.timeZoneStandard;
    lookup['tripModeID'] = newRow.tripMode;
    lookup['tripLegStatusID'] = newRow.tripLegStatusID;

    if (config.tableName === 'vehiclColor') {
      lookup.colorName = newRow.lookupName;
    }
    return lookup;
  };

  const invalidRowInfo = (row: GridValidRowModel): any | null => {
    const attributesToCheck: string[] = [];
    const invalidAttributes: string[] = [];

    /* Business Code */
    if (!currentConfig?.hideBC) {
      if (!row.lookupBC || !validateBC(`${row.lookupBC}`)) {
        return t('settings.invalidBC');
      }
    }

    /* Basic attributes */
    if (!currentConfig?.hideName) {
      attributesToCheck.push('lookupName');
    }
    if (!!currentConfig?.hasDescription) {
      attributesToCheck.push('description');
    }
    if (!!currentConfig?.hasDisplayOrder) {
      attributesToCheck.push('displayOrder');
    }
    if (!!currentConfig?.hasPriorityOrder) {
      attributesToCheck.push('priorityOrder');
    }
    if (!!currentConfig?.hasExtension) {
      attributesToCheck.push('lookupExtension');
    }
    if (!!currentConfig?.hasTimeZone) {
      attributesToCheck.push(
        'ianaStandard',
        'timeZoneDayLight',
        'timeZoneStandard',
      );
    }

    /* Table specific attributes */
    if (currentConfig?.tableName === 'stateCodeList') {
      attributesToCheck.push('stateCode', 'stateName');
    }
    if (currentConfig?.tableName === 'tripLegStatusReason') {
      attributesToCheck.push('tripLegStatusID');
    }
    if (currentConfig?.tableName === 'vehicleType') {
      attributesToCheck.push('tripMode');
    }

    attributesToCheck.forEach((attr) => {
      if (`${row[attr]}`.trim() === '') {
        invalidAttributes.push(t(`settings.${attr}`));
      }
    });

    if (invalidAttributes.length > 0) {
      if (invalidAttributes.length > 1) {
        return t('settings.multiFieldsRequired', {
          fields: invalidAttributes.join(', '),
        });
      }
      return t('settings.singleFieldRequired', {
        field: invalidAttributes[0],
      });
    }

    return null;
  };

  const processRowUpdate = (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel,
  ) => {
    const updatedRow = { ...newRow };
    const invalidInfo = invalidRowInfo(updatedRow);
    if (invalidInfo) {
      throw new Error(invalidInfo);
    }

    const isStateCodeList = currentConfig?.tableName === 'stateCodeList';
    const changed = !isEqual(newRow, oldRow);
    if (changed && currentConfig) {
      const idx = lookupValues[currentConfig.tableName].findIndex(
        (l: any) =>
          (isStateCodeList
            ? l.stateCode
            : l[`${currentConfig.tableName}BC`]) === newRow.lookupBC,
      );
      const lookup = packageLookup(currentConfig, idx, newRow);
      if (isEditingNewItem) {
        dispatch(
          lookupSlice.createLookupItemAsync(currentConfig.tableName, lookup),
        );
        setRows(rows!.filter((row) => row.lookupBC !== ''));
      } else {
        dispatch(
          lookupSlice.updateLookupItemAsync(currentConfig.tableName, lookup),
        );
      }
      endEditing();
    }
    return updatedRow;
  };

  useEffect(() => {
    if (
      storeStatus === StoreStatus.Success &&
      previousStoreStatus !== StoreStatus.Success
    ) {
      updateRows(currentConfig);
      dispatch(lookupSlice.setStatus(StoreStatus.Idle));
    }
  }, [currentConfig, storeStatus, previousStoreStatus, updateRows, dispatch]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const onAddLookup = () => {
    const newComponent: LookupRow = {
      cronJobSchedule:
        currentConfig?.tableName === 'ingestionCronJob' ? '' : undefined,
      description: currentConfig?.hasDescription ? '' : undefined,
      displayOrder: currentConfig?.hasDisplayOrder ? 0 : undefined,
      priorityOrder: currentConfig?.hasPriorityOrder ? 0 : undefined,
      isActive: true,
      isEffectiveDatesRequired: [
        'driverDocumentType',
        'vehicleDocumentType',
      ].includes(currentConfig?.tableName ?? '')
        ? false
        : undefined,
      isRequired: ['driverDocumentType', 'vehicleDocumentType'].includes(
        currentConfig?.tableName ?? '',
      )
        ? false
        : undefined,
      lookupBC: '',
      lookupExtension: currentConfig?.hasExtension ? '' : undefined,
      lookupName: '',
      reservationRetryLimit:
        currentConfig?.tableName === 'frequencyPeriod' ? 0 : undefined,
      stateCode: currentConfig?.tableName === 'stateCodeList' ? '' : undefined,
      stateName: currentConfig?.tableName === 'stateCodeList' ? '' : undefined,
      tripMode: currentConfig?.tableName === 'vehicleType' ? '' : undefined,
    };
    setRows((oldRows) => [...(oldRows || []), newComponent]);
    setRowModesModel({ ...rowModesModel, ['']: { mode: GridRowModes.Edit } });
    setIsEditing(true);
    setIsEditingNewItem(true);
  };

  const processRowUpdateError = (error: any) => {
    setErrorMessage(error.message);
    setIsEditing(true);
  };

  return (
    <>
      <PageHeader title={t('settings.lookups')} />
      <StyledBox className={classes.container}>
        <TextField
          className={classes.dropdown}
          select
          label={t('settings.chooseLookup')}
          variant="outlined"
          value={currentConfig?.tableName || ''}
          disabled={isEditing}
          onChange={(evt) => onChange(evt.target.value)}>
          {lookupConfigs?.map((lookup) => {
            return (
              <MenuItem value={lookup.tableName} key={lookup.tableName}>
                {_.startCase(lookup.tableName).trim()}
              </MenuItem>
            );
          })}
        </TextField>
        <Box className={classes.content}>
          <DataGridPro
            getRowId={(row) => row.lookupBC}
            apiRef={apiRef}
            rows={rows || []}
            localeText={{ noRowsLabel: t('settings.lookupNoRows') }}
            className={classes.grid}
            columns={isEditingNewItem ? createColumns : editColumns}
            autoHeight={true}
            rowThreshold={0}
            rowSelection={false}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableColumnSelector={true}
            disableRowSelectionOnClick={true}
            hideFooter={true}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={processRowUpdateError}
          />
          <Box component="div" className={classes.footer}>
            <FormHelperText error>{errorMessage}</FormHelperText>
            {currentConfig &&
              !currentConfig.disableAddNew &&
              !isSuperReaderUser && (
                <Tooltip title={t('settings.lookupAddRow')}>
                  <span>
                    <Fab
                      data-testid="add-lookup"
                      disabled={isEditing || isEditingNewItem}
                      onClick={onAddLookup}
                      color="primary">
                      <AddCircleOutlineOutlinedIcon />
                    </Fab>
                  </span>
                </Tooltip>
              )}
          </Box>
        </Box>
      </StyledBox>
    </>
  );
}
